// security/fraudDetector.js
// BuzzInApp — Detección de fraude
//
// Responsabilidades:
//   1. Deduplicación de clicks por IP + fingerprint
//   2. Tiempo mínimo en página (anti-bot)
//   3. Detección de mock location (Android)
//   4. Anti-collusion: % de cuentas nuevas por comercio
//   5. Helpers para leer/actualizar trust score en Firestore

import {
  clicksCol,
  redemptionsCol,
  setDocument,
  updateDocument,
  getDocument,
  query,
  where,
  getDocs,
  serverTimestamp,
  increment,
  COLLECTIONS,
} from "../config/firebase";
import {
  CLICK_VALIDATION,
  ANTI_COLLUSION,
  TRUST_SCORE,
  IDENTITY,
} from "../config/security";

// ─── CAPA 2 — Validación de clicks ───────────────────────────────────────────

/**
 * Registra un click en Firestore con deduplicación por IP.
 * Llamado desde la landing page del link compartido (no desde la app principal).
 *
 * @param {object} params
 * @param {string} params.linkId      ID único del link compartido
 * @param {string} params.sharerId    UID del usuario que compartió
 * @param {string} params.businessId  ID del comercio
 * @param {string} params.ip          IP del visitante (obtenida server-side o via api)
 * @param {string} params.fingerprint Fingerprint del dispositivo visitante
 * @param {number} params.timeOnPage  Segundos que el visitante estuvo en la landing
 * @returns {Promise<{ counted: boolean, reason: string }>}
 */
export const recordClick = async ({
  linkId,
  sharerId,
  businessId,
  ip,
  fingerprint,
  timeOnPage,
}) => {
  // 1. Tiempo mínimo en página (anti-bot)
  if (timeOnPage < CLICK_VALIDATION.MIN_TIME_ON_PAGE_SECONDS) {
    return { counted: false, reason: "too_fast" };
  }

  // 2. Deduplicación por IP
  const dedupId   = `${linkId}_${ip}`;
  const existing  = await getDocument(COLLECTIONS.CLICKS, dedupId);
  if (existing) {
    return { counted: false, reason: "duplicate_ip" };
  }

  // 3. Registrar click válido
  await setDocument(COLLECTIONS.CLICKS, dedupId, {
    linkId,
    sharerId,
    businessId,
    ip,
    fingerprint,
    timeOnPage,
    counted: true,
    createdAt: serverTimestamp(),
  });

  // 4. Incrementar contador en el documento del link
  await updateDocument("links", linkId, {
    clickCount: increment(1),
    updatedAt:  serverTimestamp(),
  });

  return { counted: true, reason: "ok" };
};

/**
 * Retorna el conteo real de clicks de un link (leído de Firestore, no de UI).
 * El staff del comercio siempre ve este número.
 * @param {string} linkId
 * @returns {Promise<number>}
 */
export const getRealClickCount = async (linkId) => {
  const q    = query(clicksCol(), where("linkId", "==", linkId), where("counted", "==", true));
  const snap = await getDocs(q);
  return snap.size;
};

// ─── CAPA 5 — Detección de mock location ─────────────────────────────────────

/**
 * Detecta si el dispositivo Android está usando una app de mock location.
 * En iOS no es posible detectarlo desde el browser — se confía en geofencing.
 *
 * Heurísticas usadas:
 *   a) navigator.userAgent para detectar Android
 *   b) Velocidad irreal entre dos posiciones GPS sucesivas
 *   c) Precisión perfecta (exactamente 0m) — característica de mocks
 *
 * @param {GeolocationPosition} position  Posición obtenida de la Geolocation API
 * @param {GeolocationPosition|null} previousPosition  Posición anterior (si existe)
 * @returns {{ isMock: boolean, confidence: "low"|"medium"|"high", flags: string[] }}
 */
export const detectMockLocation = (position, previousPosition = null) => {
  const flags = [];
  let suspicionScore = 0;

  const { accuracy, latitude, longitude } = position.coords;

  // a) Precisión exactamente 0 → característica de mocks
  if (accuracy === 0) {
    flags.push("zero_accuracy");
    suspicionScore += 3;
  }

  // b) Precisión excesivamente perfecta en Android (mocks suelen dar 1-3m exactos)
  if (accuracy !== null && accuracy < 2) {
    flags.push("suspiciously_high_accuracy");
    suspicionScore += 1;
  }

  // c) Velocidad irreal entre posiciones sucesivas
  if (previousPosition) {
    const timeDelta = (position.timestamp - previousPosition.timestamp) / 1000; // segundos
    if (timeDelta > 0 && timeDelta < 60) {
      const prevLat = previousPosition.coords.latitude;
      const prevLng = previousPosition.coords.longitude;
      const distMeters = haversineDistance(prevLat, prevLng, latitude, longitude);
      const speedKmh   = (distMeters / timeDelta) * 3.6;

      // Más de 200 km/h entre dos lecturas consecutivas → imposible caminando
      if (speedKmh > 200) {
        flags.push("impossible_speed");
        suspicionScore += 4;
      }
    }
  }

  // d) Posición exacta en coordenadas sin decimales (mocks a veces redondean)
  const latDecimals = (latitude.toString().split(".")[1] || "").length;
  const lngDecimals = (longitude.toString().split(".")[1] || "").length;
  if (latDecimals <= 2 || lngDecimals <= 2) {
    flags.push("low_precision_coordinates");
    suspicionScore += 2;
  }

  const isMock      = suspicionScore >= 3;
  const confidence  =
    suspicionScore >= 5 ? "high" :
    suspicionScore >= 3 ? "medium" : "low";

  return { isMock, confidence, flags };
};

// ─── CAPA 9 — Anti-collusion ──────────────────────────────────────────────────

/**
 * Verifica si un comercio supera el umbral de canjes de cuentas nuevas.
 * Si > 80% de los canjes del último periodo vienen de cuentas < 7 días → alerta.
 *
 * @param {string} businessId
 * @param {number} [lookbackDays=7]  Período de análisis
 * @returns {Promise<{ flagged: boolean, newAccountPct: number, total: number }>}
 */
export const checkCollusionRisk = async (businessId, lookbackDays = 7) => {
  const since = new Date();
  since.setDate(since.getDate() - lookbackDays);

  const q = query(
    redemptionsCol(),
    where("businessId", "==", businessId),
    where("createdAt", ">=", since)
  );

  const snap = await getDocs(q);
  if (snap.empty) return { flagged: false, newAccountPct: 0, total: 0 };

  let newAccountCount = 0;
  snap.forEach((doc) => {
    if (doc.data().isNewAccount) newAccountCount++;
  });

  const total          = snap.size;
  const newAccountPct  = (newAccountCount / total) * 100;
  const flagged        = newAccountPct >= ANTI_COLLUSION.NEW_ACCOUNT_REDEMPTION_THRESHOLD_PCT;

  return { flagged, newAccountPct: Math.round(newAccountPct), total };
};

// ─── CAPA 8 — Trust score ─────────────────────────────────────────────────────

/**
 * Lee el trust score actual de un usuario.
 * @param {string} uid
 * @returns {Promise<number>}
 */
export const getTrustScore = async (uid) => {
  const user = await getDocument(COLLECTIONS.USERS, uid);
  return user?.trustScore ?? TRUST_SCORE.INITIAL_SCORE;
};

/**
 * Aplica un delta al trust score de un usuario.
 * @param {string} uid
 * @param {number} delta  Puede ser positivo o negativo
 * @param {string} reason  Clave de TRUST_SCORE.DELTA (para auditoría)
 */
export const applyTrustDelta = async (uid, delta, reason) => {
  await updateDocument(COLLECTIONS.USERS, uid, {
    trustScore:    increment(delta),
    [`trustLog.${Date.now()}`]: { delta, reason }, // historial de cambios
    updatedAt:     serverTimestamp(),
  });
};

/**
 * Verifica si el trust score de un usuario es suficiente para que sus clicks pesen 100%.
 * @param {string} uid
 * @returns {Promise<{ fullWeight: boolean, score: number }>}
 */
export const checkClickWeight = async (uid) => {
  const score = await getTrustScore(uid);
  return {
    fullWeight: score >= TRUST_SCORE.MIN_SCORE_FOR_FULL_WEIGHT,
    score,
  };
};

// ─── Cuenta nueva ─────────────────────────────────────────────────────────────

/**
 * Determina si una cuenta es "nueva" según el threshold de IDENTITY.NEW_ACCOUNT_DAYS.
 * @param {Date|number} createdAt  Fecha de creación de la cuenta
 * @returns {boolean}
 */
export const isNewAccount = (createdAt) => {
  const created  = createdAt instanceof Date ? createdAt : new Date(createdAt);
  const diffDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays < IDENTITY.NEW_ACCOUNT_DAYS;
};

// ─── Utilidad: distancia Haversine ───────────────────────────────────────────
// Duplicada acá para no depender de geoFence.js en este módulo
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R    = 6371000; // metros
  const φ1   = (lat1 * Math.PI) / 180;
  const φ2   = (lat2 * Math.PI) / 180;
  const Δφ   = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ   = ((lng2 - lng1) * Math.PI) / 180;
  const a    =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
